import { useTranslation } from '@/shared/web/i18n/LanguageProvider';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { LifeBuoy, MessageCircle, Send, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import type { CreatorTicket, SupportTeamMember } from '@shared/web/types';
import { creatorApi } from '../data/creator.api';
import { SupportTeamAvatar } from './SupportTeamAvatar';
import { SupportFeedback } from './SupportFeedback';
import { useSupportTicketView } from '../hooks/useSupportTicketView';
import { Button } from '@/web/components/ui/button';
import { cn } from '@/web/lib/utils';

export function CreatorSupportWidget() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [tickets, setTickets] = useState<CreatorTicket[]>([]);
  const [teammates, setTeammates] = useState<SupportTeamMember[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<CreatorTicket | null>(null);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const selectedId = useRef<string | null>(null);
  useSupportTicketView(expanded && !composing && selected ? selected.id : null, selected?.pendingAnswerAt);

  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      try {
        const [{ tickets: rows }, status] = await Promise.all([
          creatorApi.tickets(), creatorApi.supportAvailability(),
        ]);
        if (disposed) return;
        setTickets(rows);
        setAvailable(status.available);
        setTeammates(status.teammates);
        if (selectedId.current) {
          try {
            const { ticket } = await creatorApi.ticket(selectedId.current);
            if (!disposed && selectedId.current === ticket.id) setSelected(ticket);
          } catch { /* Keep the last loaded thread until it is reachable again. */ }
        }
      } catch {
        if (!disposed) {
          setAvailable(null);
          setTeammates([]);
        }
      }
    };
    const source = new EventSource('/api/creator/support/events');
    source.addEventListener('change', () => void refresh());
    source.addEventListener('open', () => void refresh());
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 15_000);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    void refresh();
    return () => { disposed = true; source.close(); window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, []);

  const open = async (id: string) => {
    selectedId.current = id;
    setSelected(null);
    setBody('');
    setComposing(false);
    try {
      const { ticket } = await creatorApi.ticket(id);
      if (selectedId.current === id) setSelected(ticket);
    } catch { toast.error('Could not open this conversation'); }
  };
  const back = () => { selectedId.current = null; setSelected(null); setBody(''); setComposing(false); };
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!body.trim() || sending || (!composing && (!selected || selectedId.current !== selected.id))) return;
    setSending(true);
    try {
      const message = body.trim();
      const excerpt = message.replace(/\s+/g, ' ').slice(0, 80);
      const { ticket } = composing
        ? await creatorApi.openTicket({ subject: excerpt.length >= 3 ? excerpt : `Support: ${excerpt}`, body: message, priority: 'normal' })
        : await creatorApi.replyToTicket(selected!.id, message);
      selectedId.current = ticket.id;
      setSelected(ticket);
      setTickets((previous) => [ticket, ...previous.filter((row) => row.id !== ticket.id)]);
      setBody('');
      setComposing(false);
      if (!available) toast.success(t('Your message is in our support queue'));
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not send your message'); }
    finally { setSending(false); }
  };

  const resolve = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const { ticket } = await creatorApi.resolveTicket(selected.id);
      setSelected(ticket);
      setTickets((previous) => previous.map((row) => row.id === ticket.id ? ticket : row));
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not resolve the ticket'); }
    finally { setSending(false); }
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (body.trim() && !sending) {
      event.currentTarget.form?.requestSubmit();
    }
  };
  const visibleTeammates = showAll ? teammates : teammates.slice(0, 3);

  return (
    <div className="fixed right-2 bottom-2 z-40 flex flex-col items-end gap-3 sm:right-6 sm:bottom-6">
      {expanded && (
        <section aria-label={t("Chat with A2.Agency")} className="bg-card border-border flex h-[min(36rem,calc(100dvh-5rem))] w-[min(25rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-2xl border shadow-2xl">
          <header className="bg-primary text-primary-foreground flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="grid size-9 place-items-center rounded-xl bg-white/15"><LifeBuoy className="size-5" /></div>
              <div><p className="text-sm font-semibold">{t("A2 creator support")}</p>{!composing && <p className="text-xs opacity-80">{available === true ? t("Our team is available") : available === false ? t("Our team is away right now") : t("Checking team availability…")}</p>}</div>
            </div>
            <Button size="icon" variant="ghost" aria-label={t("Close chat")} onClick={() => setExpanded(false)}><X className="size-4" /></Button>
          </header>
          {!composing && (
            <div className="mx-4 mt-4">
              <div className="flex flex-wrap items-center gap-2">
                {visibleTeammates.map((member) => (
                  <SupportTeamAvatar key={member.id} id={member.id} name={member.name} available={member.available} />
                ))}
                {!showAll && teammates.length > 3 && (
                  <button type="button" className="text-muted-foreground hover:text-foreground rounded-full px-1 text-xs font-medium" onClick={() => setShowAll(true)} aria-expanded={showAll} aria-label={t('Show full support team ({count} people)', { count: teammates.length })}>
                    +{teammates.length - 3}
                  </button>
                )}
                {showAll && teammates.length > 3 && (
                  <button type="button" className="text-muted-foreground hover:text-foreground text-xs" onClick={() => setShowAll(false)}>{t("Show less")}</button>
                )}
              </div>
              {available !== true && (
                <p className="text-muted-foreground pt-3 text-xs leading-relaxed">
                  {available === false
                    ? selected
                      ? t("Our team is away right now. We’ll get back to you here in 24–48 business hours.")
                      : t("This will create a support ticket and we’ll get back to you in 24–48 business hours.")
                    : t("Checking team availability…")}
                </p>
              )}
            </div>
          )}
          {selected || composing ? (
            <>
              <div className="border-border flex items-center gap-2 border-b px-4 py-3">
                <button type="button" onClick={back} aria-label={t("Back to conversations")}><ArrowLeft className="size-4" /></button>
                <span className="truncate text-sm font-medium">{composing ? t("New conversation") : selected?.subject}</span>
                {!composing && selected?.status !== 'resolved' && <Button type="button" size="sm" variant="ghost" className="ml-auto shrink-0" disabled={sending} onClick={() => void resolve()}>{t("Resolve")}</Button>}
              </div>
              {composing ? <div className="flex-1" /> : (
                <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4" aria-live="polite">
                  {selected?.messages?.map((message) => <div key={message.id} className={cn('max-w-[88%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap', message.authorRole === 'creator' ? 'bg-primary text-primary-foreground self-end' : 'bg-secondary self-start')}><p className="mb-1 text-[11px] opacity-70">{message.authorRole === 'creator' ? t("You") : message.authorName || 'A2.Agency'}</p>{message.body}</div>)}
                </div>
              )}
              {selected?.status === 'resolved' && (
                <div className="px-3 pb-2"><SupportFeedback key={selected.id} ticket={selected} onUpdated={setSelected} /></div>
              )}
              <form onSubmit={send} className="border-border mt-auto border-t p-3">
                <div className="border-input bg-background focus-within:border-ring focus-within:ring-ring/40 flex items-end gap-2 rounded-2xl border p-1.5 pl-3 shadow-xs focus-within:ring-2">
                  <textarea
                    aria-label={t("Message to support")}
                    rows={1}
                    value={body}
                    maxLength={5000}
                    onChange={(event) => setBody(event.target.value)}
                    onKeyDown={onComposerKeyDown}
                    placeholder={composing ? t("What do you need help with?") : t("Write your message…")}
                    className="field-sizing-content max-h-32 min-h-9 min-w-0 flex-1 resize-none bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                  />
                  <Button size="icon" type="submit" className="size-8 shrink-0 rounded-full" disabled={!body.trim() || sending} aria-label={t("Send message")}><Send className="size-4" /></Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex flex-1 flex-col overflow-y-auto p-3">
              <Button onClick={() => { setComposing(true); setSelected(null); setBody(''); selectedId.current = null; }}><MessageCircle className="size-4" /> {t("Start a conversation")}</Button>
              <p className="text-muted-foreground px-1 pt-5 pb-2 text-xs font-medium uppercase tracking-wider">{t("Your conversations")}</p>
              {tickets.length === 0 && <p className="text-muted-foreground px-1 py-4 text-sm">{t("No conversations yet. We’re here to help.")}</p>}
              {tickets.map((ticket) => <button type="button" key={ticket.id} onClick={() => void open(ticket.id)} className="border-border hover:bg-secondary/50 flex flex-col gap-1 border-b px-2 py-3 text-left"><span className="truncate text-sm font-medium">{ticket.subject}</span><span className="text-muted-foreground text-xs">{ticket.status === 'resolved' ? t("Resolved") : ticket.awaiting === 'creator' ? t("We replied") : t("With A2")}</span></button>)}
            </div>
          )}
        </section>
      )}
      <Button className="shadow-lg" aria-label={t("Open creator support chat")} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}><MessageCircle className="size-4" /> {t("Chat with us")}</Button>
    </div>
  );
}
